import { type EpicAttributes } from './generated/EpicAttributes';
import { model } from '../modelBuilder';
import ApplicationModel, { attr } from '../ApplicationModel';
import { AtRiskFlaggable } from './concerns/AtRiskFlaggable';
import { TeamAssignable } from './concerns/TeamAssignable';
import { HasRecordPositions } from './concerns/HasRecordPositions';
import { HasCustomFieldValues } from './concerns/HasCustomFieldValues';
import { HasExtensionFields } from './concerns/HasExtensionFields';
import { HasEstimates } from './concerns/HasEstimates';

interface Epic
  extends EpicAttributes,
    AtRiskFlaggable,
    HasEstimates,
    HasCustomFieldValues,
    HasRecordPositions,
    TeamAssignable {}

@model
@AtRiskFlaggable
@HasCustomFieldValues
@TeamAssignable
@HasRecordPositions
@HasExtensionFields
@HasEstimates
class Epic extends ApplicationModel {
  // Start auto-generated
  static typename: 'Epic' = 'Epic';
  get typename(): 'Epic' {
    return 'Epic';
  }

  static internalTypename: 'Epic' = 'Epic';
  get internalTypename(): 'Epic' {
    return 'Epic';
  }

  static terminologyName = 'Epic';

  static inputType = 'EpicAttributes!';

  static filterType = 'EpicFilters!';
  static filters = {
    active: 'Boolean',
    id: '[ID!]',
    projectId: 'ID',
    releaseId: 'ID',
    teamId: 'ID',
    iterationId: 'ID',
    programIncrementId: 'ID',
    hasBookmarkPosition: 'HasBookmarkPositionFilter',
  };

  static fields = {
    commentsCount: attr({ readable: true }),
    createdAt: attr({ readable: true }),
    currentSchedulingTeamsCount: attr({ readable: true }),
    dependenciesCount: attr({ readable: true }),
    drillInUrl: attr({
      args: {
        publishableId: 'ID',
        publishableType: 'String',
        viewSnapshotId: 'ID',
        dashboardId: 'ID',
        exportType: 'String',
        bookmarkId: 'ID',
      },
      readable: true,
    }),
    dueDate: attr({ readable: true }),
    durationInTeamWorkflowStatus: attr({ readable: true }),
    featuresCount: attr({ readable: true }),
    goalsCount: attr({ readable: true }),
    id: attr({ readable: true }),
    initiativeId: attr({ readable: true }),
    integrationLinksCount: attr({ readable: true }),
    iterationId: attr({ readable: true }),
    manualRiskComment: attr({ writable: true, readable: true }),
    name: attr({ writable: true, readable: true }),
    path: attr({ readable: true }),
    position: attr({ writable: true, readable: true }),
    programIncrementId: attr({ readable: true }),
    progress: attr({ readable: true }),
    projectId: attr({ readable: true }),
    referenceNum: attr({ readable: true }),
    releaseId: attr({ readable: true }),
    riskFactors: attr({ readable: true, json: true }),
    riskFactorsCount: attr({ readable: true }),
    schedulingTeamsCount: attr({ readable: true }),
    score: attr({ readable: true }),
    scoringSystemId: attr({ readable: true }),
    showFeatureRemainingEstimate: attr({ readable: true }),
    startDate: attr({ readable: true }),
    tagList: attr({ readable: true }),
    teamId: attr({ readable: true }),
    teamWorkflowStatusId: attr({ readable: true }),
    updatedAt: attr({ readable: true }),
    watchersCount: attr({ readable: true }),
    workflowStatusId: attr({ readable: true }),
    assignedToUser: attr.belongsTo({
      writeShape: ['id', 'name', 'email'],
      readable: true,
      typename: 'User',
    }),
    bestEstimate: attr.belongsTo({ readable: true, typename: 'Estimate' }),
    createdByUser: attr.belongsTo({ readable: true, typename: 'User' }),
    description: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Note',
    }),
    initialEstimate: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Estimate',
    }),
    initiative: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Initiative',
    }),
    iteration: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Iteration',
    }),
    originalEstimate: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Estimate',
    }),
    permissions: attr.belongsTo({
      readable: true,
      typename: 'ResourcePermissions',
    }),
    programIncrement: attr.belongsTo({
      readable: true,
      typename: 'ProgramIncrement',
    }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
    release: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Release',
    }),
    remainingEstimate: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Estimate',
    }),
    researchFolder: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Page',
    }),
    screenDefinition: attr.belongsTo({
      readable: true,
      typename: 'ScreenDefinition',
    }),
    team: attr.belongsTo({
      writeShape: ['id', 'name', 'referencePrefix'],
      readable: true,
      typename: 'Project',
    }),
    teamWorkflowStatus: attr.belongsTo({
      writeShape: ['id', 'name', 'category'],
      readable: true,
      typename: 'WorkflowStatus',
    }),
    terminology: attr.belongsTo({
      readable: true,
      typename: 'TerminologyItem',
    }),
    workDone: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Estimate',
    }),
    workflowStatus: attr.belongsTo({
      writeShape: ['id', 'name', 'category'],
      readable: true,
      typename: 'WorkflowStatus',
    }),
    addGoals: attr.hasMany({ writable: true, readable: false }),
    bookmarksRecordPositions: attr.hasMany({
      args: { filters: 'RecordPositionFilters' },
      readable: true,
    }),
    childRecordLinks: attr.hasMany({
      args: { filters: 'RecordLinkFilters' },
      readable: true,
    }),
    comments: attr.hasMany({ readable: true }),
    currentSchedulingTeams: attr.hasMany({ readable: true }),
    customFieldValues: attr.hasMany({
      args: { ids: '[ID!]', keys: '[String!]' },
      readable: true,
    }),
    extensionFields: attr.hasMany({
      args: { filters: 'ExtensionFieldFilters' },
      readable: true,
    }),
    features: attr.hasMany({ readable: true }),
    goals: attr.hasMany({ writable: true, readable: true }),
    integrationLinks: attr.hasMany({ readable: true }),
    parentRecordLinks: attr.hasMany({
      args: { filters: 'RecordLinkFilters' },
      readable: true,
    }),
    removeGoals: attr.hasMany({ writable: true, readable: false }),
    schedulingTeams: attr.hasMany({ readable: true }),
    scoreFacts: attr.hasMany({ writable: true, readable: true }),
    tags: attr.hasMany({ readable: true }),
    tasks: attr.hasMany({ readable: true }),
    watchers: attr.hasMany({ readable: true }),
  };
  // End auto-generated
}

export default Epic;
